import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query"
import { ArrowLeftRight, Lock, Search } from "lucide-react"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { KpiMovimientos } from "@/componentes/kpi/KpiTesoreria"
import { AvisoCuentaBloqueada, esCuentaBloqueada } from "@/componentes/tesoreria/CuentaBloqueada"
import { DetalleMovimiento } from "@/componentes/tesoreria/DetalleMovimiento"
import { DialogoPin } from "@/componentes/tesoreria/DialogoPin"
import type { Cuenta, Divisa, Movimiento } from "@/componentes/tesoreria/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha, comoFechaHora, FECHA_MINIMA, hoyLocal } from "@/lib/fechas"

/**
 * El ledger de banco: cada movimiento y por qué.
 *
 * ⚠ **Las anuladas salen marcadas, no escondidas.** Esconderlas dejaría un listado
 * que cuadra con el saldo pero no explica por qué cambió, y la fila anulada es justo
 * la que contesta «¿y aquel ingreso de ayer?». Una transacción nunca se borra: se
 * anula, la fila queda y deja de contar para el saldo.
 *
 * ⚠ **Con tope de filas, y el tope crece a tandas.** Un ledger de banco crece sin
 * parar; sin límite, esta pantalla se cae sola el año que viene. Se dice cuántas se
 * están viendo en vez de dar a entender que están todas, y el botón de abajo pide otra
 * tanda hasta el techo del servidor — de ahí en adelante se acota con los filtros.
 *
 * ⚠ **El buscador es del SERVIDOR** —concepto o referencia—, no un filtro sobre lo ya
 * traído: filtrando aquí sólo se buscaría dentro de las últimas doscientas, que es
 * justo donde ya se ve todo.
 *
 * ⚠ **La fecha de la tabla es la FECHA VALOR —el día en que el banco movió el
 * dinero—, y cuándo se tecleó la fila va en su título.** Enseñaba `creado_en` mientras
 * los filtros de abajo acotaban por `fecha_valor`: una fila de septiembre registrada en
 * octubre salía al filtrar septiembre con un «01/10» al lado, y así no hay manera de
 * cuadrar nada contra el banco. Las dos fechas se conservan; lo que cambia es cuál
 * manda en la columna, y manda la del banco, igual que en el extracto.
 *
 * ⚠ **La cuenta puede venir en la dirección** (`?cuenta=10`): así se llega desde
 * «Buscar en Movimientos», en la ficha de una cuenta, con el filtro ya puesto.
 */

const HOY = hoyLocal()

/** Cuántas trae de primeras, y cuántas añade cada «ver más». */
const POR_TANDA = 200

/** El techo del servidor: `limite` admite 500 como máximo. */
const TOPE_SERVIDOR = 500

/** Sólo un id: lo demás de la dirección no es un filtro y no se manda a la API. */
function cuentaDeLaDireccion(valor: string | null): string {
  return valor !== null && /^\d+$/.test(valor) ? valor : ""
}

export function Movimientos() {
  const [direccion] = useSearchParams()
  const [cuentaId, setCuentaId] = useState(() => cuentaDeLaDireccion(direccion.get("cuenta")))
  const [desde, setDesde] = useState("")
  const [hasta, setHasta] = useState("")
  const [incluirAnuladas, setIncluirAnuladas] = useState(true)
  const [texto, setTexto] = useState("")
  const [buscar, setBuscar] = useState("")
  const [tope, setTope] = useState(POR_TANDA)

  // Se busca al dejar de teclear, no en cada letra: una petición por pulsación haría
  // parpadear el ledger mientras se escribe.
  useEffect(() => {
    const espera = window.setTimeout(() => setBuscar(texto.trim()), 300)
    return () => window.clearTimeout(espera)
  }, [texto])
  // Pulsar un movimiento abre su ficha: de dónde sale y su soporte.
  const [abierto, setAbierto] = useState<Movimiento | null>(null)
  // La cuenta con PIN que se está abriendo desde aquí.
  const [pidiendoPin, setPidiendoPin] = useState<Cuenta | null>(null)

  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
  })

  const parametros = new URLSearchParams({ limite: String(tope) })
  if (cuentaId) parametros.set("cuenta_id", cuentaId)
  if (desde) parametros.set("desde", desde)
  if (hasta) parametros.set("hasta", hasta)
  if (!incluirAnuladas) parametros.set("incluir_anuladas", "false")
  if (buscar) parametros.set("buscar", buscar)
  const hayFiltros = Boolean(cuentaId || desde || hasta || buscar)
  // Los indicadores cuentan lo mismo que la lista, sin su tope.
  const filtros = new URLSearchParams(parametros)
  filtros.delete("limite")

  const movimientos = useQuery({
    queryKey: ["movimientos", parametros.toString()],
    queryFn: () => api.get<Movimiento[]>(`/api/v1/transacciones?${parametros}`),
    // Sin esto, cada letra tecleada vaciaba la tabla a «Cargando…» y la pantalla
    // temblaba mientras se escribía.
    placeholderData: keepPreviousData,
  })

  // ⚠ **Las cuentas con PIN cerradas NO salen en el listado general**: el servidor las
  // deja fuera sin decirlo (`GET /transacciones`), y la lista parecía completa. Se
  // pregunta el estado de las que tienen PIN para AVISAR de cuáles faltan (09/10).
  const conPin = (cuentas.data ?? []).filter((c) => c.tiene_pin && c.activo)
  const estados = useQueries({
    queries: conPin.map((c) => ({
      queryKey: ["estado-cuenta", c.id],
      queryFn: () => api.get<{ abierta: boolean }>(`/api/v1/cuentas-bancarias/${c.id}/estado`),
    })),
  })
  const cerradas = conPin.filter((_, i) => estados[i]?.data?.abierta === false)
  const cuentaFiltrada = (cuentas.data ?? []).find((c) => String(c.id) === cuentaId) ?? null
  const bloqueada = esCuentaBloqueada(movimientos.error)

  const estilo = {
    borderColor: "var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--fg)",
  }
  const nombreCuenta = new Map((cuentas.data ?? []).map((c) => [c.id, c.nombre]))
  const codigoDivisa = new Map((divisas.data ?? []).map((d) => [d.id, d.codigo]))

  const subtitulo = [
    cuentaFiltrada ? `Cuenta ${cuentaFiltrada.nombre}` : "Todas las cuentas a la vista",
    desde || hasta
      ? `Fecha valor ${desde ? `desde ${comoFecha(desde)}` : ""}${desde && hasta ? " " : ""}${hasta ? `hasta ${comoFecha(hasta)}` : ""}`
      : "Todas las fechas",
    buscar ? `Búsqueda «${buscar}»` : null,
    incluirAnuladas ? "Con anuladas" : "Sin anuladas",
  ]
    .filter(Boolean)
    .join(" · ")

  const documento = (): DocumentoExportable => ({
    titulo: "Movimientos",
    subtitulo,
    secciones: [
      {
        titulo: "Movimientos",
        columnas: [
          { titulo: "Fecha valor", tipo: "fecha" },
          { titulo: "Cuenta", tipo: "texto" },
          { titulo: "Tipo", tipo: "texto" },
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Referencia", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Monto", tipo: "monto" },
        ],
        filas: (movimientos.data ?? []).map((m) => ({
          celdas: [
            m.fecha_valor,
            nombreCuenta.get(m.cuenta_id) ?? `#${m.cuenta_id}`,
            m.tipo,
            m.concepto,
            m.referencia,
            m.anulado ? `Anulada${m.motivo_anulacion ? `: ${m.motivo_anulacion}` : ""}` : null,
            codigoDivisa.get(m.divisa_id) ?? null,
            Number(m.monto),
          ],
        })),
      },
    ],
  })

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Tesorería». */}
      {movimientos.data && movimientos.data.length > 0 && (
        <div className="mb-4 flex flex-wrap justify-end gap-2">
          <Exportar titulo="Movimientos" subtitulo={subtitulo} documento={documento} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3 superficie p-3">
        <div>
          <label htmlFor="f-cuenta" className="mb-1 block text-xs font-medium uppercase">
            Cuenta
          </label>
          <select
            id="f-cuenta"
            value={cuentaId}
            onChange={(e) => {
              setCuentaId(e.target.value)
              setTope(POR_TANDA)
            }}
            className="h-8 rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          >
            <option value="">Todas</option>
            {cuentas.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-desde" className="mb-1 block text-xs font-medium uppercase">
            Desde
          </label>
          <input
            id="f-desde"
            type="date"
            min={FECHA_MINIMA}
            max={HOY}
            value={desde}
            onChange={(e) => {
              setDesde(e.target.value)
              setTope(POR_TANDA)
            }}
            className="h-8 rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>
        <div>
          <label htmlFor="f-hasta" className="mb-1 block text-xs font-medium uppercase">
            Hasta
          </label>
          <input
            id="f-hasta"
            type="date"
            min={FECHA_MINIMA}
            max={HOY}
            value={hasta}
            onChange={(e) => {
              setHasta(e.target.value)
              setTope(POR_TANDA)
            }}
            className="h-8 rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>
        <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
          <span className="mb-1 block text-xs font-medium uppercase">Buscar</span>
          <Search
            size={16}
            className="pointer-events-none absolute bottom-2 left-2.5"
            style={{ color: "var(--fg-muted)" }}
            aria-hidden={true}
          />
          <input
            type="search"
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value)
              setTope(POR_TANDA)
            }}
            placeholder="Concepto o referencia…"
            className="h-8 w-full rounded-[var(--radius-control)] border pr-2 pl-8 text-sm"
            style={estilo}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={incluirAnuladas}
            onChange={(e) => {
              setIncluirAnuladas(e.target.checked)
              setTope(POR_TANDA)
            }}
          />
          Ver anuladas
        </label>
      </div>

      <KpiMovimientos filtros={filtros} />

      {movimientos.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {!cuentaId && cerradas.length > 0 && (
        <div
          role="status"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] border p-3 text-sm"
          style={{ borderColor: "color-mix(in oklab, var(--warning) 40%, var(--border))" }}
        >
          <Lock size={16} aria-hidden={true} style={{ color: "var(--warning)" }} />
          <span className="min-w-0 flex-1">
            No se muestran los movimientos de {cerradas.map((c) => c.nombre).join(", ")}: están
            protegidas con PIN y no se han abierto en esta sesión.
          </span>
          {cerradas.map((c) => (
            <Boton key={c.id} tamano="sm" onClick={() => setPidiendoPin(c)}>
              Abrir {cerradas.length > 1 ? c.nombre : "cuenta"}
            </Boton>
          ))}
        </div>
      )}

      {bloqueada && (
        <AvisoCuentaBloqueada
          nombre={cuentaFiltrada?.nombre ?? "Esta cuenta"}
          onAbrir={() => cuentaFiltrada && setPidiendoPin(cuentaFiltrada)}
        />
      )}

      {movimientos.isError && !bloqueada && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar los movimientos
        </p>
      )}

      {movimientos.data?.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-12"
          style={{ color: "var(--fg-muted)" }}
        >
          <ArrowLeftRight size={22} aria-hidden={true} />
          <p className="text-sm">
            {hayFiltros
              ? "Ningún movimiento con esos filtros"
              : "Todavía no hay movimientos registrados"}
          </p>
        </div>
      )}

      {movimientos.data && movimientos.data.length > 0 && (
        <>
          <Tabla>
            <Cabecera>
              <Th derecha>Fecha</Th>
              <Th>Cuenta</Th>
              <Th>Tipo</Th>
              <Th>Concepto</Th>
              <Th>Referencia</Th>
              <Th derecha>Monto</Th>
            </Cabecera>
            <Cuerpo>
              {movimientos.data.map((m) => (
                <Fila
                  key={m.id}
                  data-testid={`movimiento-${m.id}`}
                  className="cursor-pointer"
                  onClick={() => setAbierto(m)}
                >
                  <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                    <span title={`Registrado el ${comoFechaHora(m.creado_en)}`}>
                      {comoFecha(m.fecha_valor)}
                    </span>
                  </Td>
                  <Td>{nombreCuenta.get(m.cuenta_id) ?? `#${m.cuenta_id}`}</Td>
                  <Td>
                    <Insignia tono={m.tipo === "INGRESO" ? "exito" : "aviso"}>{m.tipo}</Insignia>
                  </Td>
                  <Td>
                    <span style={{ opacity: m.anulado ? 0.55 : 1 }}>{m.concepto}</span>
                    {m.anulado && (
                      <span className="ml-2">
                        <Insignia tono="peligro">Anulada</Insignia>
                      </span>
                    )}
                    {m.anulado && m.motivo_anulacion && (
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {m.motivo_anulacion}
                      </span>
                    )}
                  </Td>
                  <Td style={{ color: "var(--fg-muted)" }}>{m.referencia ?? "—"}</Td>
                  <Td derecha numero>
                    <span
                      style={{
                        opacity: m.anulado ? 0.55 : 1,
                        textDecoration: m.anulado ? "line-through" : undefined,
                      }}
                    >
                      {m.monto} {codigoDivisa.get(m.divisa_id) ?? ""}
                    </span>
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
          {abierto && <DetalleMovimiento movimiento={abierto} onCerrar={() => setAbierto(null)} />}
          {/* ⚠ **Una tanda más, no una página numerada.** Con páginas habría que contar
              el ledger entero en cada carga —más caro cada año— para enseñar un número
              que a nadie le dice nada. */}
          {movimientos.data.length >= tope && tope < TOPE_SERVIDOR && (
            <div className="mt-3 flex justify-center">
              <Boton
                variante="secundario"
                onClick={() => setTope((t) => Math.min(t + POR_TANDA, TOPE_SERVIDOR))}
                disabled={movimientos.isFetching}
              >
                {movimientos.isFetching ? "Trayendo…" : "Ver más"}
              </Boton>
            </div>
          )}
          <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
            {movimientos.data.length} movimiento{movimientos.data.length === 1 ? "" : "s"}
            {movimientos.data.length >= TOPE_SERVIDOR && " (tope alcanzado: acota con los filtros)"}
          </p>
        </>
      )}
      <DialogoPin cuenta={pidiendoPin} onCerrar={() => setPidiendoPin(null)} />
    </div>
  )
}
